/**
 * A policy's versions: the immutable snapshots publishing writes.
 *
 * Read straight from Firestore, as the policy itself is. The rules let any
 * member read a version: what was in force last year is not secret from the
 * people it applied to, and "show me the version that was current on that
 * date" is a question a surveyor asks a nurse, not an administrator.
 */

import { useQuery } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, getFirestore, orderBy, query } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { toAttachments, toIsoDate, type Attachment } from './usePolicy';
import { toMomentString } from './useReviewHistory';

const db = getFirestore(app);

export type VersionStatus = 'published' | 'superseded' | 'retired' | 'other';

export interface PolicyVersion {
  id: string;
  versionNumber: number;
  status: VersionStatus;
  /**
   * The title this version was PUBLISHED under, which a later rename does not
   * change. A printed copy of the version in force three years ago has to
   * carry the name it had then, not the one the policy goes by today.
   */
  title: string | null;
  changeSummary: string | null;
  /** ISO dates. */
  publishedAt: string | null;
  effectiveDate: string | null;
  supersededAt: string | null;
  attachments: Attachment[];
  /** Which attachment is the document. Null on versions published before B6. */
  documentId: string | null;

  /**
   * PROVENANCE, WHICH THE RECORD HAS ALWAYS HELD AND THE CLIENT DISCARDED.
   *
   * Publication writes all of this and the mapper read none of it, so the
   * history table could only show an effective date and a change summary.
   * Publication and effect are DIFFERENT FACTS — a policy approved in March
   * and effective in April has two dates and the table used to fall back from
   * one to the other silently, printing a single ambiguous column.
   *
   * Full moments, not days: "who approved this and when, to the minute" is the
   * question, and truncating it to a date threw the answer away. Everything
   * here is null on a record that never stored it, and nothing is inferred.
   */
  publishedAtMoment: string | null;
  approvedAtMoment: string | null;
  publishedByUid: string | null;
  approvedByUid: string | null;
  /** Resolved against the member list where possible; never invented. */
  publishedByName: string | null;
  approvedByName: string | null;
  changeNotes: string | null;
  /** The round of approval this version came out of, if it came from one. */
  approvalInstanceId: string | null;
  previousVersionId: string | null;
  /** Which version replaced this one. Publication and retirement name it differently. */
  supersededByVersionId: string | null;
}

function toStatus(v: unknown): VersionStatus {
  return v === 'published' || v === 'superseded' || v === 'retired' ? v : 'other';
}

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * AN IMPORTED VERSION'S FILES, READ AS ATTACHMENTS.
 *
 * The library import writes `publishedPdf` and `sourceDocx` on the version
 * instead of an attachments array, so an imported published policy rendered
 * "No document attached" over a document that was sitting in the bucket. This
 * is a read adapter and nothing more: no record is rewritten, the ids are the
 * field names, and `getPolicyFileUrl` resolves them on the server under the
 * same authorization as every other version file. A file the import failed to
 * store has no `storagePath` and is correctly not offered.
 */
export function withImportedFiles(mapped: Attachment[], d: Record<string, unknown>): Attachment[] {
  if (mapped.length > 0) return mapped;
  const out: Attachment[] = [];
  for (const [id, type, fallback] of [
    ['publishedPdf', 'application/pdf', 'published.pdf'],
    ['sourceDocx', DOCX, 'source.docx'],
  ] as const) {
    const f = d[id] as { storagePath?: unknown; fileName?: unknown } | undefined;
    if (!f || typeof f !== 'object' || typeof f.storagePath !== 'string' || !f.storagePath) continue;
    out.push({
      id,
      name: typeof f.fileName === 'string' && f.fileName ? f.fileName : fallback,
      contentType: type,
      size: null,
      storagePath: f.storagePath,
      url: null,
      generatedFrom: null,
    });
  }
  return out;
}

export function toVersion(
  id: string,
  d: Record<string, unknown>,
  names?: ReadonlyMap<string, string>,
): PolicyVersion {
  const who = (uid: string | null) => (uid ? names?.get(uid) ?? null : null);
  return {
    id,
    versionNumber: typeof d.versionNumber === 'number' ? d.versionNumber : 0,
    status: toStatus(d.status),
    title: text(d.title),
    changeSummary: typeof d.changeSummary === 'string' && d.changeSummary ? d.changeSummary : null,
    publishedAt: toIsoDate(d.publishedAt),
    effectiveDate: toIsoDate(d.effectiveDate),
    supersededAt: toIsoDate(d.supersededAt),
    attachments: withImportedFiles(toAttachments(d.attachments), d),
    documentId: typeof d.documentId === 'string' && d.documentId ? d.documentId : null,
    publishedAtMoment: toMomentString(d.publishedAt),
    approvedAtMoment: toMomentString(d.approvedAt),
    publishedByUid: text(d.publishedBy),
    approvedByUid: text(d.approvedBy),
    publishedByName: who(text(d.publishedBy)),
    approvedByName: who(text(d.approvedBy)),
    changeNotes: text(d.changeNotes),
    approvalInstanceId: text(d.approvalInstanceId),
    previousVersionId: text(d.previousVersionId),
    // Publication writes `supersededByVersionId`; retirement writes `supersededBy`.
    supersededByVersionId: text(d.supersededByVersionId) ?? text(d.supersededBy),
  };
}

/** In words, for a row. A version with no stored status is the older kind
 *  publishing wrote before it recorded one; the current one is whichever the
 *  policy points at, which the caller knows. */
export function versionStatusLabel(v: PolicyVersion, currentId: string | null): string {
  if (v.id === currentId) return 'Current';
  if (v.status === 'superseded') return 'Superseded';
  if (v.status === 'retired') return 'Retired';
  return 'Earlier';
}

/**
 * Names for the uids a version records, read from the member list every member
 * may read. A uid nobody can resolve stays a uid rather than becoming a guess.
 */
async function memberNames(orgId: string): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  try {
    const snap = await getDocs(collection(db, 'orgs', orgId, 'members'));
    for (const d of snap.docs) {
      const data = d.data();
      const name = typeof data.displayName === 'string' && data.displayName ? data.displayName : data.email;
      if (typeof name === 'string' && name) map.set(d.id, name);
    }
  } catch {
    // Not fatal: the row shows what it can prove and no name.
  }
  return map;
}

export function usePolicyVersions(orgId: string | null, policyId: string | undefined, allowed = true) {
  return useQuery({
    queryKey: ['policy-versions', orgId, policyId],
    // A Firestore rule refuses the whole listing when the organization does
    // not show earlier versions to this person, and a refused read is an
    // error on the page rather than an empty list. Do not ask.
    enabled: Boolean(orgId && policyId) && allowed,
    queryFn: async (): Promise<PolicyVersion[]> => {
      const [snap, names] = await Promise.all([
        getDocs(query(collection(db, 'orgs', orgId!, 'policies', policyId!, 'versions'), orderBy('versionNumber', 'desc'))),
        memberNames(orgId!),
      ]);
      return snap.docs.map((d) => toVersion(d.id, d.data(), names));
    },
  });
}

export function usePolicyVersion(orgId: string | null, policyId: string | undefined, versionId: string | null) {
  return useQuery({
    queryKey: ['policy-version', orgId, policyId, versionId],
    enabled: Boolean(orgId && policyId && versionId),
    retry: false,
    queryFn: async (): Promise<PolicyVersion | null> => {
      const snap = await getDoc(doc(db, 'orgs', orgId!, 'policies', policyId!, 'versions', versionId!));
      return snap.exists() ? toVersion(snap.id, snap.data()) : null;
    },
  });
}
